import { Injectable } from '@angular/core';
import { Workbook, Worksheet } from 'exceljs';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { IExcelColumnConfig } from 'src/app/models/excel-column-config.model';
import { IOHTrainExcel } from 'src/app/models/ioh-train-excel.model';
import { IOHAppService } from 'src/app/services/ioh-app.service';
import { IOHFilterFormService } from 'src/app/services/filter/ioh-filter-form.service';
import { environment } from 'src/environments/environment';
import {
  addHeaderToExistingSheet,
  cellOverflowHidden,
  freezeHeaderRow,
  saveAsExcelFile,
  setCellBackgroundColor,
  setCellBorders,
  setCellFont
} from 'src/app/shared/utils/ioh-filter-excel-generator';
import {
  getODTODColor,
  getTrainStoppedTimeColor,
  getHOSColor,
  getPerformanceColor,
  getPerformanceStatusColor,
  formatDuration,
  hexToArgb,
  COLOR_PALETTE
} from 'src/app/constants/ioh-excel-colors.constant';

@Injectable({
  providedIn: 'root'
})
export class IOHFilterExcelService {
  private readonly trainReportApi = `${environment.appConfig.api.reportApi}/train-report`;

  constructor(
    private iohAppService: IOHAppService,
    private iohFilterFormService: IOHFilterFormService,
    private http: HttpClient
  ) {}

  async downloadCurrentLevelData(level: number): Promise<void> {
    try {
      let intermodalTrains: any[] = [];
      let apiTrains: any[] = [];
      const checkedSources = 'Cache, API';

      const cachedTrains = this.getCachedTrainsData(level);
      const cachedIntermodalTrains = cachedTrains.filter(train => this.isIntermodalTrain(train));

      if (cachedIntermodalTrains.length > 0) {
        intermodalTrains = cachedIntermodalTrains;
      }

      try {
        const response = await firstValueFrom(this.http.get<any>(this.trainReportApi));
        apiTrains = this.parseApiResponse(response);
        const apiIntermodalTrains = apiTrains.filter(train => this.isIntermodalTrain(train));

        if (intermodalTrains.length === 0 && apiIntermodalTrains.length > 0) {
          intermodalTrains = apiIntermodalTrains;
        }
      } catch (apiError) {
        if (intermodalTrains.length === 0) {
          intermodalTrains = cachedIntermodalTrains;
        }
      }

      const enrichedIntermodalTrains = intermodalTrains.map(train => this.enrichAndValidateTrain(train));

      const terminalTrainsList = enrichedIntermodalTrains.filter(train => {
        const normalizedTrainState = this.normalizeTrainState(train?.trainState);
        return normalizedTrainState === 'TRAIN_STATE_AT_ORIGIN' || normalizedTrainState === 'AT_ORIGIN';
      });

      const level2MapLorTrains = this.getLevel2MapLorTrains();
      const mappedLorTrains = this.mapTrainsWithApiByTrainId(level2MapLorTrains, apiTrains);
      const lorTrains = mappedLorTrains.map(train => this.enrichAndValidateTrain(train));

      const terminalTrains = terminalTrainsList;
      const filteredLorTrains = this.filterLorTrainsForExport(lorTrains);

      const terminalRows = terminalTrains.map((train, idx) => 
        this.toExportRow(train, `Terminal-${idx + 1}`)
      );
      
      const lorRows = this.deduplicateRowsByTrainId(
        filteredLorTrains.map((train, idx) => this.toExportRow(train, `LOR-${idx + 1}`))
      );

      if (terminalRows.length === 0 && lorRows.length === 0) {
        throw new Error(`No train data available for export. Checked ${checkedSources}. Please ensure data is loaded and try again.`);
      }

      const workbook = new Workbook();
      this.buildTerminalSheet(workbook, terminalRows);
      this.buildLorSheet(workbook, lorRows);

      const dateString = moment().format('MMDDYYYYHHmm');
      const fileName = `IOH-VIEW-Data-${dateString}`;

      await saveAsExcelFile(workbook, fileName);
    } catch (error) {
      const errorMessage = (error as any)?.message || 'Failed to download Excel file. Please try again.';
      throw new Error(errorMessage);
    }
  }

  private parseApiResponse(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response.trains && Array.isArray(response.trains)) {
      return response.trains;
    }

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }

    if (response.trainList && Array.isArray(response.trainList)) {
      return response.trainList;
    }

    return [];
  }

  private isIntermodalTrain(train: any): boolean {
    return this.iohFilterFormService.isIntermodalTrain(train);
  }

  private getCachedTrainsData(level: number): any[] {
    const cachedTrains: any[] = [];

    const terminals = this.iohAppService.iohTerminalsLevels[level] || [];
    
    terminals.forEach((terminal: any) => {
      const awaitingDeparture = terminal.trainAwaitingDeparture || [];
      const nearByTrains = terminal.trainOrders || [];
      
      awaitingDeparture.forEach((train: any) => {
        train.terminalCode = train.terminalCode || terminal.code || '-';
        train.terminalName = train.terminalName || terminal.name || '-';
        if (!train.trainState && !train.state && !train.trainStatus && !train.status) {
          train.trainState = 'TRAIN_STATE_AT_ORIGIN';
        }
      });
      nearByTrains.forEach((train: any) => {
        train.terminalCode = train.terminalCode || terminal.code || '-';
        train.terminalName = train.terminalName || terminal.name || '-';
      });
      
      cachedTrains.push(...awaitingDeparture, ...nearByTrains);
    });

    const corridors = this.iohAppService.iohCorridorsLevels[level] || [];
    
    corridors.forEach((corridor: any) => {
      const trains = corridor.trainOrders || [];
      
      trains.forEach((train: any) => {
        train.corridorCode = train.corridorCode || corridor.code || '-';
        train.corridorName = train.corridorName || corridor.name || '-';
      });
      
      cachedTrains.push(...trains);
    });

    return cachedTrains;
  }

  private enrichAndValidateTrain(train: any): IOHTrainExcel {
    const commodity = this.extractCommodity(train);

    let trainMovementLabel = 'Unknown';
    const normalizedTrainState = this.normalizeTrainState(
      train.trainState || train.state || train.trainStatus || train.status
    );

    if (train.trainStopped || train.stopped) {
      trainMovementLabel = 'Stopped';
    } else if (normalizedTrainState === 'TRAIN_STATE_AT_ORIGIN' || normalizedTrainState === 'AT_ORIGIN') {
      trainMovementLabel = 'At Origin';
    } else {
      trainMovementLabel = 'Moving';
    }

    const stoppedMinutes = train.stoppedTimeInMinutes ?? train.stoppedTime ?? train.StoppedTime ?? 0;
    const trainStoppedTimeFormat = formatDuration(stoppedMinutes);

    const lastGpsCity = train.lastGpsCity || train.lastGPSCity || train.gpsCity || '-';
    const lastGpsState = train.lastGpsState || train.lastGPSState || train.gpsState || '-';
    const lastGpsEventTime = train.lastGpsEventTime || train.gpsEventTime || train.lastGpsTime || '-';

    const lastOsCity = train.lastOsCity || train.lastOPSystemCity || train.osCity || lastGpsCity || '-';
    const lastOsState = train.lastOsState || train.lastOPSystemState || train.osState || lastGpsState || '-';
    const lastOsEventTime = train.lastOsEventTime || train.osEventTime || lastGpsEventTime || '-';

    const averageMPH = train.averageMPH || train.avgSpeed || train.avgMPH || 0;
    const lorMPH = train.lorMPH || train.currentSpeed || train.speed || 0;
    const plannedAverageMPH = train.plannedAverageMPH || train.scheduledSpeed || 0;
    const plannedLorMPH = train.plannedLorMPH || train.plannedSpeed || 0;

    const crewOnDutyTime = train.crewOnDutyTime || train.crewTime || train.CrewTime || '-';
    const crewHoursOfServiceTime = train.crewHoursOfServiceTime || train.crewHours || train.CrewHours || '-';
    const crewStatus = train.lorCrewHosInd || train.crewStatus || train.CrewStatus || '-';

    const odtodInMinutes = train.odtodInMinutes ?? train.odtod ?? train.ODTOD ?? 0;
    const odtod = formatDuration(odtodInMinutes);
    const odtodColor = getODTODColor(odtodInMinutes);

    const nextCrewODTODInMinutes = train.nextCrewODTODInMinutes ?? train.nextOdtod ?? 0;
    const nextCrewODTOD = formatDuration(nextCrewODTODInMinutes);
    const nextCrewODTODColor = getODTODColor(nextCrewODTODInMinutes);
    const nextCrewOnDutyTime = train.nextCrewOnDutyTime || train.nextCrewTime || train.NextCrewTime || '-';

    const nextStationCity = train.nextStationCity || train.nextCity || '-';
    const nextStationState = train.nextStationState || train.nextState || '-';
    const nextStationScheduledTime = train.nextStationScheduledTime || train.nextEta || '-';
    const nextStationAdjustedTime = train.nextStationAdjustedTime || train.nextAdjustedEventTime || train.nextAdjustedTime || '-';

    const originStationCity = train.originStationCity || train.originCity || '-';
    const originStationState = train.originStationState || train.originState || '-';
    const destinationStationCity = train.destinationStationCity || train.destCity || '-';
    const destinationStationState = train.destinationStationState || train.destState || '-';

    const lastReportedTons = train.lastReportedTons || train.weight || 0;
    const lastReportedLength = train.lastReportedLength || train.length || train.Length || 0;
    const engineCount = train.engineCount || train.engines || 0;
    const workingHpTt = train.workingHpTt || train.hp || '-';

    const performanceValue = train.performance || train.performancePercent || 0;
    const performanceStr = typeof performanceValue === 'number' ? `${performanceValue}%` : performanceValue;
    const performanceStatus = train.performanceStatus;
    const performanceColor = getPerformanceColor(performanceValue);
    const performanceClassification =
      train.performanceClassification || 'PERFORMANCE_CLASSIFICATION_UNSPECIFIED';

    const crewOnDutyColor = getHOSColor(crewHoursOfServiceTime);
    const stoppedTimeColor = getTrainStoppedTimeColor(stoppedMinutes);

    const enrichedTrain: IOHTrainExcel = {
      trainId: train.trainId || train.id || train.train_id || '-',
      trainClassCode: train.trainClassCode,
      trainTypeCode: train.trainTypeCode,
      trainState: normalizedTrainState || '-',
      commodityDesc: commodity,
      trainMovementLabel: trainMovementLabel,
      trainStopped: !!(train.trainStopped || train.stopped || train.Stopped),
      stoppedTimeInMinutes: stoppedMinutes,
      trainStoppedTimeFormat: trainStoppedTimeFormat,
      trainStoppedTimeChipStyles: {
        background: stoppedTimeColor,
        color: COLOR_PALETTE.white
      },

      lastGpsCity: lastGpsCity,
      lastGpsState: lastGpsState,
      lastGpsEventTime: lastGpsEventTime,

      lastOsCity: lastOsCity,
      lastOsState: lastOsState,
      lastOsEventTime: lastOsEventTime,

      averageMPH: averageMPH,
      lorMPH: lorMPH,
      plannedAverageMPH: plannedAverageMPH,
      plannedLorMPH: plannedLorMPH,

      performance: performanceStr,
      performanceStatus: performanceStatus,
      performanceClassification: performanceClassification,
      performanceColor: performanceColor,
      lastReportedPerformance: train.lastReportedPerformance || '',

      crewOnDutyTime: crewOnDutyTime,
      crewOnDutyColor: crewOnDutyColor,
      crewTime: crewOnDutyTime,
      crewHoursOfServiceTime: crewHoursOfServiceTime,
      odtod: odtod,
      odtodInMinutes: odtodInMinutes,
      odtodColor: odtodColor,
      lorCrewHosInd: crewStatus,

      isNextCrewAvailable: !!train.nextCrewODTODInMinutes,
      nextCrewOnDutyTime: nextCrewOnDutyTime,
      nextCrewODTOD: nextCrewODTOD,
      nextCrewODTODColor: nextCrewODTODColor,
      nextCrew: {
        label: 'Next Crew',
        time: nextCrewOnDutyTime
      },

      nextStationCity: nextStationCity,
      nextStationState: nextStationState,
      nextStationScheduledTime: nextStationScheduledTime,
      nextStationAdjustedTime: nextStationAdjustedTime,
      originStationCity: originStationCity,
      originStationState: originStationState,
      destinationStationCity: destinationStationCity,
      destinationStationState: destinationStationState,

      lastReportedTons: lastReportedTons,
      lastReportedLength: lastReportedLength,
      engineCount: engineCount,
      workingHpTt: workingHpTt,
      onLastCrewLeg: !!(train.onLastCrewLeg || train.lastCrewLeg),
      reportedLengthClassification: train.reportedLengthClassification || '-',
      trainGroups: Array.isArray(train.trainGroups) ? train.trainGroups : [],
      upsTrain: !!train.upsTrain,

      source: train.source || 'API'
    };

    return enrichedTrain;
  }

  private normalizeTrainState(trainState: any): string {
    return String(trainState || '').trim().toUpperCase();
  }

  private extractCommodity(train: any): string {
    if (!train) {
      return '-';
    }

    const mappedCommodity = this.iohFilterFormService.resolveTrainCommodity(train);
    if (mappedCommodity) {
      return mappedCommodity;
    }

    const candidateFields = [
      'commodityDesc',
      'commodity',
      'commodityDescription',
      'commodityCode',
      'commodityName',
      'commodityType',
      'commodity_desc',
      'commodity_code',
      'commodity_description',
      'commodity_name',
      'commodity_type',
      'CommodityDesc',
      'Commodity',
      'CommodityDescription',
      'CommodityCode',
      'CommodityName',
      'CommodityType',
      'commodity.desc',
      'commodity.name',
      'commodity.code',
      'commodity.description',
      'goods',
      'cargo',
      'lading',
      'manifest',
      'cargoDesc',
      'cargoType',
      'commodity_info',
      'CommodityInfo',
      'commodityInfo'
    ];

    for (const field of candidateFields) {
      if (field.includes('.')) {
        const parts = field.split('.');
        let value = train[parts[0]];
        for (let i = 1; i < parts.length; i++) {
          value = value?.[parts[i]];
        }
        if (value && value !== '' && value !== null && value !== undefined) {
          return String(value);
        }
      } else {
        const value = train[field];
        if (value && value !== '' && value !== null && value !== undefined) {
          return String(value);
        }
      }
    }

    return '-';
  }

  private buildTerminalSheet(workbook: Workbook, rows: IOHTrainExcel[]): void {
    const worksheet = workbook.addWorksheet('Terminal');
    this.buildTrainSheet(worksheet, rows);
  }

  private buildLorSheet(workbook: Workbook, rows: IOHTrainExcel[]): void {
    const worksheet = workbook.addWorksheet('LOR');
    this.buildTrainSheet(worksheet, rows);
  }

  private buildTrainSheet(worksheet: Worksheet, rows: IOHTrainExcel[]): void {
    const config = this.buildTrainColumnConfig();
    addHeaderToExistingSheet(worksheet, config);
    freezeHeaderRow(worksheet, 1);

    rows.forEach(item => {
      const row = worksheet.addRow([
        item.trainId,
        this.getTrainPerformanceDisplayValue(item),
        item.commodityDesc,
        item.trainMovementLabel,
        item.trainStoppedTimeFormat,
        this.buildLocationCell(item.lastOsCity, item.lastOsState),
        item.lastOsEventTime,
        this.buildLocationCell(item.lastGpsCity, item.lastGpsState),
        item.lastGpsEventTime,
        item.averageMPH,
        item.plannedAverageMPH,
        item.lorMPH,
        item.plannedLorMPH,
        item.crewOnDutyTime,
        item.odtod,
        item.crewHoursOfServiceTime,
        this.getNextCrewStatusTime(item, 'R'),
        this.getNextCrewStatusTime(item, 'C'),
        this.getNextCrewStatusTime(item, 'D'),
        item.nextCrewODTOD,
        this.buildLocationCell(item.nextStationCity, item.nextStationState),
        item.nextStationScheduledTime,
        item.nextStationAdjustedTime,
        this.buildLocationCell(item.originStationCity, item.originStationState),
        this.buildLocationCell(item.destinationStationCity, item.destinationStationState),
        item.lastReportedTons,
        item.lastReportedLength,
        item.engineCount,
        item.workingHpTt,
        item.onLastCrewLeg ? 'Y' : 'N'
      ]);

      row.eachCell((cell, colNumber) => {
        setCellBorders(cell);
        cellOverflowHidden(cell);

        if (colNumber === 2) {
          const perfColor =
            getPerformanceStatusColor(item.performanceStatus) ||
            getPerformanceStatusColor(item.performanceClassification) ||
            item.performanceColor ||
            getPerformanceColor(item.performance);
          const argbColor = hexToArgb(perfColor);
          setCellBackgroundColor(cell, argbColor);
          setCellFont(cell, false, 'FFFFFFFF');
        } else if (colNumber === 5) {
          const stoppedColor = item.trainStoppedTimeChipStyles?.background || COLOR_PALETTE.white;
          const argbColor = hexToArgb(stoppedColor);
          setCellBackgroundColor(cell, argbColor);
          setCellFont(cell, false, 'FFFFFFFF');
        } else if (colNumber === 15) {
          const odtodColor = item.odtodColor;
          const argbColor = hexToArgb(odtodColor);
          setCellBackgroundColor(cell, argbColor);
          setCellFont(cell, false, 'FFFFFFFF');
        } else if (colNumber === 16) {
          const hosColor = item.crewOnDutyColor;
          const argbColor = hexToArgb(hosColor);
          setCellBackgroundColor(cell, argbColor);
          setCellFont(cell, false, 'FFFFFFFF');
        } else if (colNumber === 20) {
          const nextOdtodColor = item.nextCrewODTODColor;
          const argbColor = hexToArgb(nextOdtodColor);
          setCellBackgroundColor(cell, argbColor);
          setCellFont(cell, false, 'FFFFFFFF');
        } else {
          setCellFont(cell, false, 'FF000000');
        }
      });
    });
  }

  private buildTrainColumnConfig(): IExcelColumnConfig[] {
    return [
      { header: 'Train ID', width: 16 },
      { header: 'Train Performance', width: 20 },
      { header: 'Commodity', width: 20 },
      { header: 'Moving/Stopped', width: 16 },
      { header: 'Train Stopped Time', width: 18 },
      { header: 'Last OS Location', width: 24 },
      { header: 'Last OS Date/Time', width: 18 },
      { header: 'Last GPS Location', width: 24 },
      { header: 'Last GPS Date/Time', width: 18 },
      { header: 'Avg MPH', width: 10 },
      { header: 'Plan MPH', width: 12 },
      { header: 'LOR MPH', width: 10 },
      { header: 'Plan LOR MPH', width: 14 },
      { header: 'Current Crew On Duty Time', width: 20 },
      { header: 'Current Crew ODTOD', width: 16 },
      { header: 'Current Crew HOS', width: 16 },
      { header: 'Next Crew Request', width: 18 },
      { header: 'Next Crew Called', width: 18 },
      { header: 'Next Crew On Duty', width: 18 },
      { header: 'Next Crew ODTOD', width: 16 },
      { header: 'Next Scheduled Event City/State', width: 24 },
      { header: 'Next Scheduled Event Time', width: 20 },
      { header: 'Next Adjusted Event Time', width: 20 },
      { header: 'Origin City / State', width: 20 },
      { header: 'Destination City / State', width: 20 },
      { header: 'Last Rpt Tons', width: 14 },
      { header: 'Current Length', width: 14 },
      { header: 'Engines', width: 10 },
      { header: 'HP / TT', width: 12 },
      { header: 'Last Crew Leg', width: 14 }
    ];
  }

  private toExportRow(train: IOHTrainExcel, source: string): IOHTrainExcel {
    return {
      trainId: train.trainId,
      trainClassCode: train.trainClassCode,
      trainTypeCode: train.trainTypeCode,
      trainState: train.trainState,
      commodityDesc: train.commodityDesc,
      trainMovementLabel: train.trainMovementLabel,
      trainStopped: train.trainStopped,
      stoppedTimeInMinutes: train.stoppedTimeInMinutes,
      trainStoppedTimeFormat: train.trainStoppedTimeFormat,
      trainStoppedTimeChipStyles: train.trainStoppedTimeChipStyles,
      lastGpsCity: train.lastGpsCity,
      lastGpsState: train.lastGpsState,
      lastGpsEventTime: train.lastGpsEventTime,
      lastOsCity: train.lastOsCity,
      lastOsState: train.lastOsState,
      lastOsEventTime: train.lastOsEventTime,
      averageMPH: train.averageMPH,
      lorMPH: train.lorMPH,
      plannedAverageMPH: train.plannedAverageMPH,
      plannedLorMPH: train.plannedLorMPH,
      performance: train.performance,
      performanceStatus: train.performanceStatus,
      performanceClassification: train.performanceClassification,
      performanceColor: train.performanceColor,
      lastReportedPerformance: train.lastReportedPerformance,
      crewOnDutyTime: train.crewOnDutyTime,
      crewOnDutyColor: train.crewOnDutyColor,
      crewTime: train.crewTime,
      crewHoursOfServiceTime: train.crewHoursOfServiceTime,
      odtod: train.odtod,
      odtodInMinutes: train.odtodInMinutes,
      odtodColor: train.odtodColor,
      lorCrewHosInd: train.lorCrewHosInd,
      isNextCrewAvailable: train.isNextCrewAvailable,
      nextCrewOnDutyTime: train.nextCrewOnDutyTime,
      nextCrewODTOD: train.nextCrewODTOD,
      nextCrewODTODColor: train.nextCrewODTODColor,
      nextCrew: train.nextCrew,
      nextStationCity: train.nextStationCity,
      nextStationState: train.nextStationState,
      nextStationScheduledTime: train.nextStationScheduledTime,
      nextStationAdjustedTime: train.nextStationAdjustedTime,
      originStationCity: train.originStationCity,
      originStationState: train.originStationState,
      destinationStationCity: train.destinationStationCity,
      destinationStationState: train.destinationStationState,
      lastReportedTons: train.lastReportedTons,
      lastReportedLength: train.lastReportedLength,
      engineCount: train.engineCount,
      workingHpTt: train.workingHpTt,
      onLastCrewLeg: train.onLastCrewLeg,
      reportedLengthClassification: train.reportedLengthClassification,
      trainGroups: train.trainGroups,
      upsTrain: train.upsTrain,
      source: source
    };
  }

  private deduplicateRowsByTrainId(rows: IOHTrainExcel[]): IOHTrainExcel[] {
    const rowMap = new Map<string, IOHTrainExcel>();
    let fallbackIndex = 0;

    rows.forEach(row => {
      if (row.trainId && row.trainId !== '-') {
        rowMap.set(row.trainId, row);
        return;
      }
      rowMap.set(`${row.source}-${fallbackIndex++}`, row);
    });

    return [...rowMap.values()];
  }

  private getLevel2MapLorTrains(): any[] {
    const mapTrains = Array.isArray(this.iohAppService.iohl3Trains)
      ? this.iohAppService.iohl3Trains
      : [];

    const mapLorTrains = mapTrains.filter(train => !train?.inTerminal);
    if (mapLorTrains.length > 0) {
      return this.deduplicateByTrainId(mapLorTrains);
    }

    const level2Corridors = this.iohAppService.iohCorridorsLevels?.[1] || [];
    const level2CorridorTrains = level2Corridors.flatMap(
      (corridor: any) => corridor?.trains || []
    );

    return this.deduplicateByTrainId(level2CorridorTrains);
  }

  private mapTrainsWithApiByTrainId(baseTrains: any[], apiTrains: any[]): any[] {
    if (!Array.isArray(baseTrains) || baseTrains.length === 0) {
      return [];
    }

    const apiTrainMap = new Map<string, any>();
    (apiTrains || []).forEach(train => {
      const key = this.getTrainIdKey(train);
      if (key) {
        apiTrainMap.set(key, train);
      }
    });

    return baseTrains.map(train => {
      const key = this.getTrainIdKey(train);
      if (!key) {
        return train;
      }

      const apiTrain = apiTrainMap.get(key);
      return apiTrain ? { ...train, ...apiTrain } : train;
    });
  }

  private deduplicateByTrainId(trains: any[]): any[] {
    const trainMap = new Map<string, any>();
    let fallbackIndex = 0;

    (trains || []).forEach(train => {
      const key = this.getTrainIdKey(train) || `fallback-${fallbackIndex++}`;
      trainMap.set(key, train);
    });

    return [...trainMap.values()];
  }

  private getTrainIdKey(train: any): string {
    const rawTrainId =
      train?.trainId || train?.id || train?.train_id || train?.train6 || '';
    return String(rawTrainId).trim().toUpperCase();
  }

  private filterLorTrainsForExport(trains: IOHTrainExcel[]): IOHTrainExcel[] {
    const filterCriteria = this.iohFilterFormService.iohFilterFormGroup.getRawValue();
    const normalizedSearch = String(filterCriteria.iohInputForSearch || '').trim().toLowerCase();

    return trains.filter(train => {
      const normalizedTrain = {
        ...train,
        trainGroups: Array.isArray(train.trainGroups) ? train.trainGroups : [],
        upsTrain: !!train.upsTrain,
        reportedLengthClassification: train.reportedLengthClassification || '-',
        lastReportedPerformance: train.lastReportedPerformance || ''
      };
      const trainIdMatched =
        !normalizedSearch ||
        String(train.trainId || '').trim().toLowerCase() === normalizedSearch;

      if (!trainIdMatched) {
        return false;
      }

      if (
        train.performanceClassification ===
        'PERFORMANCE_CLASSIFICATION_UNSPECIFIED'
      ) {
        return false;
      }

      const trainPerformanceMatched = this.iohAppService.iohIsTrainPerformanceMatched(
        normalizedTrain as any,
        filterCriteria
      );
      const trainCommodityMatched = this.iohAppService.iohIsCommodityMatched(
        normalizedTrain as any,
        filterCriteria
      );
      const trainGroupMatched = this.iohAppService.iohIsTrainGroupMatched(
        normalizedTrain as any,
        filterCriteria
      );
      const trainStoppedMatched = this.iohAppService.isTrainStoppageMatched(
        normalizedTrain as any,
        filterCriteria
      );
      const trainLengthMatched = this.iohAppService.isTrainLengthMatched(
        normalizedTrain as any,
        filterCriteria
      );

      return (
        trainPerformanceMatched &&
        trainCommodityMatched &&
        trainGroupMatched &&
        trainStoppedMatched &&
        trainLengthMatched
      );
    });
  }

  private buildLocationCell(city?: string, state?: string): string {
    const normalizedCity = city && city !== '-' ? city : '';
    const normalizedState = state && state !== '-' ? state : '';

    if (normalizedCity && normalizedState) {
      return `${normalizedCity}, ${normalizedState}`;
    }

    return normalizedCity || normalizedState || '-';
  }

  private getTrainPerformanceDisplayValue(train: IOHTrainExcel): string {
    const performance = train.performance && train.performance !== '-' ? train.performance : '';
    const status = train.performanceStatus && train.performanceStatus !== '-' ? train.performanceStatus : '';
    return [performance, status].filter(Boolean).join(' ') || '-';
  }

  private getNextCrewStatusTime(train: IOHTrainExcel, status: string): string {
    if (!train.isNextCrewAvailable || train.lorCrewHosInd !== status) {
      return '-';
    }

    return train.nextCrew?.time || train.nextCrewOnDutyTime || '-';
  }
}
